import { useState, useEffect } from 'react';
import { etsyAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Copy, Check, Loader2, DollarSign, Edit3, Save, X, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TennisPricingPanel } from '../components/listings/TennisPricingPanel';
import { isTennis, TENNIS_CONFIG } from '../lib/tennis';

// =============================================================
// Pricing constants — brand-wide. Edit here to change.
// =============================================================
export const PRICING = {
  metals: [
    { key: 'silver',   label: 'Silver',     metalCost: 280 },
    { key: '10k',      label: '10K Gold',   metalCost: 350 },
    { key: '14k',      label: '14K Gold',   metalCost: 420 },
    { key: '18k',      label: '18K Gold',   metalCost: 600 },
    { key: 'platinum', label: 'Platinum',   metalCost: 750 },
  ],
  // Pendant / studs — lighter metal table than rings.
  pendantMetals: [
    { key: 'silver',   label: 'Silver',     metalCost: 100 },
    { key: '10k',      label: '10K Gold',   metalCost: 175 },
    { key: '14k',      label: '14K Gold',   metalCost: 225 },
    { key: '18k',      label: '18K Gold',   metalCost: 300 },
    { key: 'platinum', label: 'Platinum',   metalCost: 275 },
  ],
  carats: [1, 1.5, 2, 2.5, 3, 4],
  centerStonePerCaratStandard: 150,
  centerStonePerCaratSpecialty: 250,
  settingStonePerCarat: 120,
  settingCost: 150,
  shipping: 50,
  profit: 700,
  // Band gold cost — varies by chosen metal purity. Used for standalone
  // wedding bands AND for the band component of an engagement-ring set.
  bandGoldCosts: {
    silver: 200,
    '10k': 300,
    '14k': 400,
    '18k': 600,
    platinum: 630,
  },
  bandGoldDefaultKey: '14k',
  bandSettingStonePerCarat: 200,
  bandSettingCost: 150,
  bandProfitInSet: 200,
  bandProfitStandalone: 250,
  weddingBandShipping: 25,
  // Pendant / Studs
  pendantSettingCost: 100,
  pendantProfit: 200,
  pendantShipping: 50,
};

// Pendant / studs use a wider carat range than rings.
export const PENDANT_CARATS = [0.25, 0.5, 1, 2, 3, 4, 5, 8, 10];

export const PRODUCT_TYPES = [
  { value: 'engagement_ring',     label: 'Engagement Ring',     desc: 'Single engagement ring' },
  { value: 'engagement_ring_set', label: 'Engagement Ring Set', desc: 'Ring + matching wedding band' },
  { value: 'wedding_band',        label: 'Wedding Band',        desc: 'Standalone wedding band' },
  { value: 'pendant_studs',       label: 'Pendant / Studs',     desc: 'Pendant or stud earrings' },
  { value: 'tennis_bracelet',     label: 'Tennis Bracelet',     desc: '3–15 CT · 14K white/yellow · typed prices' },
  { value: 'tennis_necklace',     label: 'Tennis Necklace',     desc: '5–25 CT · 14K white/yellow · typed prices' },
];

// Marketplace-specific fees. Etsy 6%, eBay 13%.
const MARKETPLACE_FEES = {
  etsy: 0.06,
  ebay: 0.13,
};

const fmt = (n) => `$${Math.round(n).toLocaleString('en-US')}`;

export function caratsForProductType(productType) {
  return productType === 'pendant_studs' ? PENDANT_CARATS : PRICING.carats;
}

export function metalsForProductType(productType) {
  return productType === 'pendant_studs' ? PRICING.pendantMetals : PRICING.metals;
}

export function computePrice(metalCost, carat, settingStoneCarat, feePct, opts = {}) {
  const productType = opts.productType || 'engagement_ring';
  const isSpecialty = !!opts.isSpecialtyCut;
  const bandSettingStoneCarat = opts.bandSettingStoneCarat || 0;
  const metalKey = (opts.metalKey || '').toLowerCase();
  const csRate = isSpecialty ? PRICING.centerStonePerCaratSpecialty : PRICING.centerStonePerCaratStandard;
  const bandGold = PRICING.bandGoldCosts[metalKey] ?? PRICING.bandGoldCosts[PRICING.bandGoldDefaultKey];

  let ringMetal = 0, centerStone = 0, settingStone = 0, ringSetting = 0, ringProfit = 0;
  let bandGoldCost = 0, bandSettingStone = 0, bandSetting = 0, bandProfit = 0;
  let shipping = 0;

  if (productType === 'wedding_band') {
    bandGoldCost = bandGold;
    bandSettingStone = bandSettingStoneCarat * PRICING.bandSettingStonePerCarat;
    bandSetting = PRICING.bandSettingCost;
    bandProfit = PRICING.bandProfitStandalone;
    shipping = PRICING.weddingBandShipping;
  } else if (productType === 'engagement_ring_set') {
    ringMetal = metalCost;
    centerStone = carat * csRate;
    settingStone = (settingStoneCarat || 0) * PRICING.settingStonePerCarat;
    ringSetting = PRICING.settingCost;
    ringProfit = PRICING.profit;
    bandGoldCost = bandGold;
    bandSettingStone = bandSettingStoneCarat * PRICING.bandSettingStonePerCarat;
    bandSetting = PRICING.bandSettingCost;
    bandProfit = PRICING.bandProfitInSet;
    shipping = PRICING.shipping;
  } else if (productType === 'pendant_studs') {
    // Pendant / studs — metal varies by metal (lighter table than rings).
    ringMetal = metalCost;
    centerStone = carat * csRate;
    settingStone = (settingStoneCarat || 0) * PRICING.settingStonePerCarat;
    ringSetting = PRICING.pendantSettingCost;
    ringProfit = PRICING.pendantProfit;
    shipping = PRICING.pendantShipping;
  } else {
    ringMetal = metalCost;
    centerStone = carat * csRate;
    settingStone = (settingStoneCarat || 0) * PRICING.settingStonePerCarat;
    ringSetting = PRICING.settingCost;
    ringProfit = PRICING.profit;
    shipping = PRICING.shipping;
  }

  const subtotal =
    ringMetal + centerStone + settingStone + ringSetting + ringProfit +
    bandGoldCost + bandSettingStone + bandSetting + bandProfit + shipping;
  const fee = subtotal * feePct;
  const total = subtotal + fee;
  return {
    metalCost: ringMetal,
    centerStone,
    settingStone,
    settingCost: ringSetting,
    ringProfit,
    bandGold,
    bandSettingStone,
    bandSettingCost: bandSetting,
    bandProfit,
    shipping,
    subtotal,
    fee,
    total,
  };
}

// =============================================================
// PricingTable component
//
// Props:
//   product       — the etsy_products doc
//   onUpdate      — callback after setting_stone_carat_weight save
//   marketplace   — "etsy" | "ebay" (controls fee % + accent colour + title)
// =============================================================
export default function PricingTable({ product, onUpdate, marketplace = 'etsy' }) {
  const auth = useAuth();
  const isFounder = !!auth.isFounder?.();
  const feePct = MARKETPLACE_FEES[marketplace] ?? MARKETPLACE_FEES.etsy;
  const accent = marketplace === 'ebay'
    ? {
        panel: 'bg-blue-50/40 border-blue-100',
        label: 'text-blue-800',
        sub: 'text-blue-700/70',
        cellOpen: 'bg-blue-100',
        cellHover: 'hover:bg-blue-50',
        breakdownBorder: 'border-blue-200',
        breakdownLabel: 'text-blue-700',
        dollarColor: 'text-blue-600',
      }
    : {
        panel: 'bg-emerald-50/40 border-emerald-100',
        label: 'text-emerald-800',
        sub: 'text-emerald-700/70',
        cellOpen: 'bg-emerald-100',
        cellHover: 'hover:bg-emerald-50',
        breakdownBorder: 'border-emerald-200',
        breakdownLabel: 'text-emerald-700',
        dollarColor: 'text-emerald-600',
      };

  const initialCarat = Number(product?.setting_stone_carat_weight || 0);
  const [stoneCarat, setStoneCarat] = useState(initialCarat);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(initialCarat));
  const [saving, setSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [openCell, setOpenCell] = useState(null);

  // Product type, specialty cut, band-stone carat — drive the new pricing branches
  const productType = product?.product_type || 'engagement_ring';
  const isSpecialty = !!product?.is_specialty_cut;
  const initialBandCarat = Number(product?.band_setting_stone_carat_weight || 0);
  const [bandCarat, setBandCarat] = useState(initialBandCarat);
  const [bandEditing, setBandEditing] = useState(false);
  const [bandDraft, setBandDraft] = useState(String(initialBandCarat));
  const [bandSaving, setBandSaving] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const showBandInput = productType === 'engagement_ring_set' || productType === 'wedding_band';

  useEffect(() => {
    const v = Number(product?.setting_stone_carat_weight || 0);
    setStoneCarat(v);
    setDraft(String(v));
  }, [product?.setting_stone_carat_weight]);

  useEffect(() => {
    const v = Number(product?.band_setting_stone_carat_weight || 0);
    setBandCarat(v);
    setBandDraft(String(v));
  }, [product?.band_setting_stone_carat_weight]);

  const handleSave = async () => {
    const v = parseFloat(draft);
    if (Number.isNaN(v) || v < 0 || v > 10) {
      toast.error('Enter a number between 0 and 10');
      return;
    }
    try {
      setSaving(true);
      await etsyAPI.editPricing(product.id, { setting_stone_carat_weight: v });
      setStoneCarat(v);
      setEditing(false);
      toast.success('Pricing updated');
      if (onUpdate) onUpdate({ ...product, setting_stone_carat_weight: v });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleBandSave = async () => {
    const v = parseFloat(bandDraft);
    if (Number.isNaN(v) || v < 0 || v > 10) {
      toast.error('Enter a number between 0 and 10');
      return;
    }
    try {
      setBandSaving(true);
      await etsyAPI.editPricing(product.id, { band_setting_stone_carat_weight: v });
      setBandCarat(v);
      setBandEditing(false);
      toast.success('Pricing updated');
      if (onUpdate) onUpdate({ ...product, band_setting_stone_carat_weight: v });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setBandSaving(false);
    }
  };

  const updateProductType = async (next) => {
    if (next === productType) return;
    try {
      setSavingType(true);
      await etsyAPI.editPricing(product.id, { product_type: next });
      toast.success('Product type updated — regenerate the title & description to match');
      if (onUpdate) onUpdate({ ...product, product_type: next });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update product type');
    } finally {
      setSavingType(false);
    }
  };

  const updateSpecialtyCut = async (next) => {
    if (next === isSpecialty) return;
    try {
      setSavingType(true);
      await etsyAPI.editPricing(product.id, { is_specialty_cut: next });
      toast.success(next ? 'Specialty cut enabled' : 'Specialty cut disabled');
      if (onUpdate) onUpdate({ ...product, is_specialty_cut: next });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingType(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(String(text));
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1200);
    toast.success('Copied');
  };

  const rows = metalsForProductType(productType).map(metal => {
    const cells = caratsForProductType(productType).map(c => ({
      key: `${metal.key}-${c}`,
      carat: c,
      ...computePrice(metal.metalCost, c, stoneCarat, feePct, {
        productType,
        isSpecialtyCut: isSpecialty,
        bandSettingStoneCarat: bandCarat,
        metalKey: metal.key,
      }),
    }));
    return { metal, cells };
  });

  // Standalone wedding bands have no carat dimension — emit one price per metal.
  const wbRows = metalsForProductType('wedding_band').map((metal) => ({
    metal,
    ...computePrice(0, 0, 0, feePct, {
      productType: 'wedding_band',
      isSpecialtyCut: isSpecialty,
      bandSettingStoneCarat: bandCarat,
      metalKey: metal.key,
    }),
  }));

  const csRate = isSpecialty ? PRICING.centerStonePerCaratSpecialty : PRICING.centerStonePerCaratStandard;

  const marketplaceLabel = marketplace === 'ebay' ? 'eBay' : 'Etsy';
  const blockTestId = marketplace === 'ebay' ? 'ebay-pricing-block' : 'etsy-pricing-block';

  const typePicker = (
    <div className="flex-1 min-w-[240px]">
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${accent.label} mb-1.5`}>Product type</div>
      <div className="flex flex-wrap gap-1.5">
        {PRODUCT_TYPES.map((opt) => (
          <button
            key={opt.value}
            disabled={savingType}
            onClick={() => updateProductType(opt.value)}
            className={`text-left rounded-md border px-3 py-1.5 text-[12px] transition-colors ${
              productType === opt.value
                ? `${accent.cellOpen} ${accent.label} border-transparent font-medium`
                : 'bg-white border-zinc-200 text-zinc-700 hover:border-zinc-300'
            }`}
            data-testid={`product-type-${marketplace}-${opt.value}`}
            title={opt.desc}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (isTennis(productType)) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid={blockTestId}>
        <div>
          <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
            <DollarSign size={15} className={accent.dollarColor} /> {marketplaceLabel} Pricing · {TENNIS_CONFIG[productType].label}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            {marketplace === 'etsy' ? 'Prices you type are the final Etsy prices. Only IGI certification adds +$350.' : 'Tennis pieces are priced on the Etsy tab.'}
          </p>
        </div>
        <div className={`${accent.panel} border rounded-lg p-3`} data-testid={`pricing-config-${marketplace}`}>{typePicker}</div>
        {marketplace === 'etsy'
          ? <TennisPricingPanel product={product} onUpdate={onUpdate} />
          : <div className="text-[12px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2" data-testid="tennis-ebay-note">Tennis pricing is set up for Etsy only for now.</div>}
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid={blockTestId}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
            <DollarSign size={15} className={accent.dollarColor} /> {marketplaceLabel} Pricing
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            All-in price by metal × center stone carat
            {isFounder
              ? ` — formula: metal + center stone + setting stone + setting + shipping + profit + ${(feePct * 100).toFixed(0)}% ${marketplaceLabel} fee.`
              : '.'}
          </p>
        </div>
      </div>

      {/* Product type + specialty cut + band stones — feed the pricing formula */}
      <div className={`${accent.panel} border rounded-lg p-3 space-y-3`} data-testid={`pricing-config-${marketplace}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          {typePicker}
          <div className="flex flex-col gap-1.5">
            <div className={`text-[10px] uppercase tracking-wider font-semibold ${accent.label}`}>Specialty cut?</div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isSpecialty}
                disabled={savingType}
                onChange={(e) => updateSpecialtyCut(e.target.checked)}
                className="h-4 w-4 accent-zinc-900"
                data-testid={`specialty-cut-toggle-${marketplace}`}
              />
              <span className="text-[12px] text-zinc-700">
                {isSpecialty
                  ? <>Yes — center stone <strong>${PRICING.centerStonePerCaratSpecialty}/ct</strong></>
                  : <>No — center stone <strong>${PRICING.centerStonePerCaratStandard}/ct</strong></>}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Setting stone carat — visible to everyone (engagement-ring side stones) */}
      {productType !== 'wedding_band' && (
      <div className={`${accent.panel} border rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap`}>
        <div className="flex items-center gap-3">
          <span className={`text-[10px] uppercase tracking-wider font-semibold ${accent.label}`}>
            Ring setting stone carat
          </span>
          {editing ? (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.05"
                min="0"
                max="10"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setEditing(false); setDraft(String(stoneCarat)); } }}
                className="h-8 w-28 text-sm bg-white"
                autoFocus
                data-testid={`setting-stone-input-${marketplace}`}
              />
              <span className="text-xs text-zinc-500">ct</span>
              <Button size="sm" disabled={saving} onClick={handleSave} className="h-8 bg-zinc-900 hover:bg-zinc-800 text-white">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(String(stoneCarat)); }} className="h-8">
                <X size={12} />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono text-zinc-900" data-testid={`setting-stone-display-${marketplace}`}>
                {stoneCarat || 0} ct
              </span>
              <button
                onClick={() => setEditing(true)}
                className={`${accent.label} hover:opacity-80`}
                title="Edit"
                data-testid={`edit-setting-stone-btn-${marketplace}`}
              >
                <Edit3 size={12} />
              </button>
              {isFounder && (
                <span className={`text-[11px] ${accent.sub}`}>
                  = {fmt(stoneCarat * PRICING.settingStonePerCarat)} side stones
                </span>
              )}
            </div>
          )}
        </div>
        <div className="text-[11px] text-zinc-500">
          Auto-extracted from the description. Edit if needed.
        </div>
      </div>
      )}

      {/* Band setting stone carat — only when there's a band (set or wedding band only) */}
      {showBandInput && (
        <div className={`${accent.panel} border rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap`}>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${accent.label}`}>
              Band setting stone carat
            </span>
            {bandEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.05"
                  min="0"
                  max="10"
                  value={bandDraft}
                  onChange={(e) => setBandDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleBandSave(); if (e.key === 'Escape') { setBandEditing(false); setBandDraft(String(bandCarat)); } }}
                  className="h-8 w-28 text-sm bg-white"
                  autoFocus
                  data-testid={`band-stone-input-${marketplace}`}
                />
                <span className="text-xs text-zinc-500">ct</span>
                <Button size="sm" disabled={bandSaving} onClick={handleBandSave} className="h-8 bg-zinc-900 hover:bg-zinc-800 text-white">
                  {bandSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setBandEditing(false); setBandDraft(String(bandCarat)); }} className="h-8">
                  <X size={12} />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-zinc-900" data-testid={`band-stone-display-${marketplace}`}>
                  {bandCarat || 0} ct
                </span>
                <button
                  onClick={() => setBandEditing(true)}
                  className={`${accent.label} hover:opacity-80`}
                  title="Edit"
                  data-testid={`edit-band-stone-btn-${marketplace}`}
                >
                  <Edit3 size={12} />
                </button>
                {isFounder && (
                  <span className={`text-[11px] ${accent.sub}`}>
                    = {fmt(bandCarat * PRICING.bandSettingStonePerCarat)} band stones (@ ${PRICING.bandSettingStonePerCarat}/ct)
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="text-[11px] text-zinc-500">
            {productType === 'wedding_band' ? 'Standalone band — pricing fixed across metals.' : 'Matching band — pricing added on top of the engagement ring.'}
          </div>
        </div>
      )}

      {/* Pricing matrix — engagement ring / set show full metal × carat grid. */}
      {productType !== 'wedding_band' ? (
      <div className="overflow-x-auto rounded-lg border border-zinc-200">
        <table className="w-full text-sm" data-testid={`${marketplace}-pricing-table`}>
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-600 px-3 py-2.5 sticky left-0 bg-zinc-50 z-10">
                Metal
              </th>
              {caratsForProductType(productType).map(c => (
                <th key={c} className="text-right text-[10px] uppercase tracking-wider font-semibold text-zinc-600 px-3 py-2.5">
                  {c} ct
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ metal, cells }) => (
              <tr key={metal.key} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/40">
                <td className="px-3 py-2.5 font-medium text-zinc-900 sticky left-0 bg-white z-10">
                  <div className="flex flex-col">
                    <span>{metal.label}</span>
                    {isFounder && (
                      <span className="text-[10px] text-zinc-400 font-normal">metal: {fmt(metal.metalCost)}</span>
                    )}
                  </div>
                </td>
                {cells.map(c => {
                  const cellKey = c.key;
                  const isOpen = openCell === cellKey;
                  return (
                    <td key={cellKey} className="px-2 py-1.5 text-right">
                      <div
                        className={`group relative inline-flex items-center justify-end gap-1 px-2 py-1 rounded transition-colors ${
                          isFounder ? `cursor-pointer ${isOpen ? accent.cellOpen : accent.cellHover}` : ''
                        }`}
                        onClick={() => isFounder && setOpenCell(isOpen ? null : cellKey)}
                        data-testid={`price-cell-${marketplace}-${cellKey}`}
                      >
                        <span className="font-mono text-sm font-semibold text-zinc-900">
                          {fmt(c.total)}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); copy(Math.round(c.total), cellKey); }}
                          className="text-zinc-300 hover:text-zinc-900 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Copy price"
                          data-testid={`copy-price-${marketplace}-${cellKey}`}
                        >
                          {copiedKey === cellKey
                            ? <Check size={11} className="text-emerald-600 opacity-100" />
                            : <Copy size={11} />}
                        </button>
                        {isFounder && (
                          <ChevronDown
                            size={11}
                            className={`text-zinc-400 transition-transform ${isOpen ? `rotate-180 ${accent.label}` : ''}`}
                          />
                        )}
                      </div>
                      {isFounder && isOpen && (
                        <BreakdownPanel
                          cell={c}
                          feePct={feePct}
                          csRate={csRate}
                          productType={productType}
                          borderClass={accent.breakdownBorder}
                          labelClass={accent.breakdownLabel}
                          marketplaceLabel={marketplaceLabel}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      ) : (
        // Wedding band only — price varies by metal purity (no carat dimension).
        <div className="border border-zinc-200 rounded-lg overflow-hidden" data-testid={`${marketplace}-wedding-band-table`}>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-zinc-600 px-3 py-2.5">Metal</th>
                <th className="text-right text-[10px] uppercase tracking-wider font-semibold text-zinc-600 px-3 py-2.5">All-in price</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {wbRows.map((r) => {
                const cellKey = `wb-${r.metal.key}`;
                return (
                  <tr key={r.metal.key} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/40">
                    <td className="px-3 py-2.5 font-medium text-zinc-900">
                      <div className="flex flex-col">
                        <span>{r.metal.label}</span>
                        {isFounder && (
                          <span className="text-[10px] text-zinc-400 font-normal">band gold: {fmt(r.bandGold)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-zinc-900" data-testid={`price-cell-${marketplace}-${cellKey}`}>
                      {fmt(r.total)}
                    </td>
                    <td className="pr-3 py-2.5 text-right">
                      <button
                        onClick={() => copy(Math.round(r.total), cellKey)}
                        className="text-zinc-300 hover:text-zinc-900"
                        title="Copy price"
                        data-testid={`copy-price-${marketplace}-${cellKey}`}
                      >
                        {copiedKey === cellKey
                          ? <Check size={12} className="text-emerald-600" />
                          : <Copy size={12} />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isFounder && (
            <div className="px-3 py-2 bg-zinc-50/60 border-t border-zinc-100 text-[11px] text-zinc-500">
              No center stone · Band stones {fmt(PRICING.bandSettingStonePerCarat)}/ct · Labor {fmt(PRICING.bandSettingCost)} · Profit {fmt(PRICING.bandProfitStandalone)} · Shipping {fmt(PRICING.weddingBandShipping)} · {marketplaceLabel} fee {(feePct * 100).toFixed(0)}%
            </div>
          )}
        </div>
      )}

      {isFounder && (
        <div className="text-[11px] text-zinc-400 leading-relaxed">
          {productType === 'wedding_band' ? (
            <>
              Standalone band · Per-metal gold{' '}
              <code className="bg-zinc-100 px-1 rounded">Silver $200</code>{' '}
              <code className="bg-zinc-100 px-1 rounded">10K $300</code>{' '}
              <code className="bg-zinc-100 px-1 rounded">14K $400</code>{' '}
              <code className="bg-zinc-100 px-1 rounded">18K $600</code>{' '}
              <code className="bg-zinc-100 px-1 rounded">Pt $630</code> · Band stones{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.bandSettingStonePerCarat}/ct</code> · Labor{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.bandSettingCost}</code> · Profit{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.bandProfitStandalone}</code> · Shipping{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.weddingBandShipping}</code> · {marketplaceLabel} fee{' '}
              <code className="bg-zinc-100 px-1 rounded">{(feePct * 100).toFixed(0)}%</code>.
              <span className="ml-1 italic">No center stone — band stones drive carat-tier pricing if present.</span>
            </>
          ) : productType === 'engagement_ring_set' ? (
            <>
              Ring + matching band (band metal = ring metal) · Center stone{' '}
              <code className="bg-zinc-100 px-1 rounded">${csRate}/ct</code>{isSpecialty ? ' (specialty)' : ''} · Setting{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.settingCost}</code> · Profit{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.profit}</code> · + Per-metal band gold{' '}
              <code className="bg-zinc-100 px-1 rounded">S $200</code>{' '}
              <code className="bg-zinc-100 px-1 rounded">10K $300</code>{' '}
              <code className="bg-zinc-100 px-1 rounded">14K $400</code>{' '}
              <code className="bg-zinc-100 px-1 rounded">18K $600</code>{' '}
              <code className="bg-zinc-100 px-1 rounded">Pt $630</code> · Band stones{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.bandSettingStonePerCarat}/ct</code> · Band setting{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.bandSettingCost}</code> · Band profit{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.bandProfitInSet}</code> · Shipping{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.shipping}</code> · {marketplaceLabel} fee{' '}
              <code className="bg-zinc-100 px-1 rounded">{(feePct * 100).toFixed(0)}%</code>.
            </>
          ) : productType === 'pendant_studs' ? (
            <>
              Pendant / studs · Metal Silver{' '}
              <code className="bg-zinc-100 px-1 rounded">$100</code> · 10K{' '}
              <code className="bg-zinc-100 px-1 rounded">$175</code> · 14K{' '}
              <code className="bg-zinc-100 px-1 rounded">$225</code> · 18K{' '}
              <code className="bg-zinc-100 px-1 rounded">$300</code> · Platinum{' '}
              <code className="bg-zinc-100 px-1 rounded">$275</code> · Center stone{' '}
              <code className="bg-zinc-100 px-1 rounded">${csRate}/ct</code>{isSpecialty ? ' (specialty)' : ''} · Side stones{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.settingStonePerCarat}/ct</code> · Setting{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.pendantSettingCost}</code> · Profit{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.pendantProfit}</code> · Shipping{' '}
              <code className="bg-zinc-100 px-1 rounded">${PRICING.pendantShipping}</code> · {marketplaceLabel} fee{' '}
              <code className="bg-zinc-100 px-1 rounded">{(feePct * 100).toFixed(0)}%</code>.
            </>
          ) : (
            <>
              Center stone <code className="bg-zinc-100 px-1 rounded">${csRate}/ct</code>{isSpecialty ? ' (specialty)' : ''} · Setting{' '}
              <code className="bg-zinc-100 px-1 rounded">$150</code> · Shipping{' '}
              <code className="bg-zinc-100 px-1 rounded">$50</code> · Profit{' '}
              <code className="bg-zinc-100 px-1 rounded">$700</code> · {marketplaceLabel} fee{' '}
              <code className="bg-zinc-100 px-1 rounded">{(feePct * 100).toFixed(0)}%</code>. Click any cell for the full breakdown.
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BreakdownPanel({ cell, feePct, csRate, productType, borderClass, labelClass, marketplaceLabel }) {
  const Row = ({ label, value, bold }) => (
    <div className={`flex items-center justify-between gap-3 ${bold ? 'font-semibold text-zinc-900' : ''}`}>
      <span className={bold ? '' : 'text-zinc-500'}>{label}</span>
      <span className="font-mono">{fmt(value)}</span>
    </div>
  );
  const isWeddingBand = productType === 'wedding_band';
  const isSet = productType === 'engagement_ring_set';
  const isPendant = productType === 'pendant_studs';
  const itemLabel = isPendant ? 'Pendant / studs' : 'Engagement ring';
  return (
    <div className={`absolute right-0 mt-2 z-30 w-80 bg-white border ${borderClass} rounded-lg shadow-lg p-3 text-xs space-y-1.5 text-left`}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${labelClass} mb-1.5`}>
        Cost breakdown{isWeddingBand ? '' : ` · ${cell.carat} ct`}
      </div>
      {!isWeddingBand && (
        <>
          <div className={`text-[9px] uppercase tracking-wider font-semibold text-zinc-400 mt-1`}>{itemLabel}</div>
          <Row label={isPendant ? 'Metal (pendant)' : 'Metal'} value={cell.metalCost} />
          <Row label={`Center stone (${cell.carat} × $${csRate})`} value={cell.centerStone} />
          <Row label="Side stones" value={cell.settingStone} />
          <Row label="Setting (labor)" value={cell.settingCost} />
          <Row label={isPendant ? 'Profit' : 'Ring profit'} value={cell.ringProfit} />
        </>
      )}
      {(isWeddingBand || isSet) && (
        <>
          <div className={`text-[9px] uppercase tracking-wider font-semibold text-zinc-400 mt-2`}>
            {isWeddingBand ? 'Wedding band' : 'Matching band'}
          </div>
          <Row label="Band gold (fixed)" value={cell.bandGold} />
          <Row label="Band setting stones" value={cell.bandSettingStone} />
          <Row label="Band setting (labor)" value={cell.bandSettingCost} />
          <Row label="Band profit" value={cell.bandProfit} />
        </>
      )}
      <Row label="Shipping" value={cell.shipping} />
      <div className="border-t border-zinc-100 my-1" />
      <Row label="Subtotal" value={cell.subtotal} />
      <Row label={`${marketplaceLabel} fee (${(feePct * 100).toFixed(0)}%)`} value={cell.fee} />
      <div className={`border-t mt-1 pt-1.5 ${borderClass}`}>
        <Row label="Total" value={cell.total} bold />
      </div>
    </div>
  );
}
